<template>
  <div
    class="to-complete-table"
    @mousedown="onRootMouseDown"
  >
    <p class="to-complete-table__note">
      <i class="el-icon-info" />
      {{ $t('monitoringDashboard.toComplete.multiAnalysisNote') }}
    </p>

    <el-row
      v-if="canManageNotes && selectedRows.length"
      class="to-complete-table__selection"
      type="flex"
      align="middle"
      :gutter="20"
    >
      <el-col :span="24">
        <span class="to-complete-table__selection-count">
          {{ selectedRows.length }}
        </span>
        <span class="to-complete-table__selection-label">
          {{ $t('monitoringDashboard.toComplete.notes.selected') }}
        </span>
        <el-button
          type="primary"
          size="mini"
          icon="el-icon-edit"
          @click="openBulkDialog"
        >
          {{ $t('monitoringDashboard.toComplete.notes.assign') }}
        </el-button>
      </el-col>
    </el-row>

    <el-table
      ref="table"
      v-loading="loading"
      class="to-complete-table__table"
      :data="rows"
      size="small"
      border
      row-key="id"
      :default-sort="defaultSort"
      :empty-text="emptyText"
      :row-class-name="rowClassName"
      @sort-change="debouncedSort"
      @row-click="onRowClick"
      @selection-change="onSelectionChange"
    >
      <el-table-column
        v-if="canManageNotes"
        type="selection"
        reserve-selection
      />

      <el-table-column
        v-for="column in columns"
        :key="column.key"
        :prop="column.key"
        :label="$t(`${i18nBase}.${column.label}`)"
        :min-width="column.minWidth"
        :sortable="column.sortable"
      >
        <template slot-scope="{ row }">
          <span v-if="column.type === 'date'">
            <span v-if="row[column.key]">
              {{ row[column.key] | date('DD/MM/YYYY') }}
            </span>
            <span v-else>{{ placeholder }}</span>
          </span>
          <span v-else-if="column.type === 'encoded'">
            <el-tag
              v-if="isFilled(row[column.key])"
              size="mini"
              :type="tagType(column, row[column.key])"
            >
              {{ $t(`enums.${column.enum}.${column.decode(row[column.key])}`) }}
            </el-tag>
            <span v-else>{{ placeholder }}</span>
          </span>
          <span v-else-if="column.type === 'boolean'">
            <el-tag
              v-if="row[column.key]"
              size="mini"
              type="warning"
            >
              {{ $t(`${i18nBase}.${column.label}`) }}
            </el-tag>
            <span v-else>{{ placeholder }}</span>
          </span>
          <span
            v-else-if="column.key === 'daysRemaining'"
            :class="{
              'to-complete-table__days--expired': isExpired(row),
            }"
          >
            {{ daysRemainingLabel(row) }}
          </span>
          <div
            v-else-if="column.editable"
            @click.stop
          >
            <el-input
              v-if="isEditingNote(row)"
              ref="noteInput"
              v-model="editingNote.value"
              type="textarea"
              :rows="2"
              :maxlength="noteMaxLength"
              @blur="onNoteBlur"
              @keydown.native.esc="onNoteCancel"
              @keydown.native.ctrl.enter="saveNote"
            />
            <template v-else>
              <div @click="startNoteEdit(row, column.key)">
                {{ cellText(row[column.key]) }}
              </div>
              <el-tooltip
                v-if="hasNoteMeta(row)"
                placement="top"
                popper-class="to-complete-note-tooltip"
              >
                <template slot="content">
                  <div>
                    {{ $t('monitoringDashboard.toComplete.notes.updatedBy') }}:
                    {{ noteAuthor(row) }}
                  </div>
                  <div>
                    {{ $t('monitoringDashboard.toComplete.notes.updatedAt') }}:
                    <span v-if="row.noteUpdatedAt">
                      {{ row.noteUpdatedAt | date('DD/MM/YYYY') }}
                    </span>
                    <span v-else>{{ placeholder }}</span>
                  </div>
                </template>
                <el-tag
                  class="to-complete-table__note-meta"
                  size="mini"
                  type="info"
                >
                  <span v-if="row.noteUpdatedAt">
                    {{ row.noteUpdatedAt | date('DD/MM/YYYY') }}
                  </span>
                  <span v-else>{{ placeholder }}</span>
                </el-tag>
              </el-tooltip>
            </template>
          </div>
          <span v-else>{{ cellText(row[column.key]) }}</span>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="to-complete-table__pagination"
      layout="prev, pager, next"
      :total="total"
      :page-size="limit"
      :current-page="page"
      @current-change="onPageChange"
    />

    <el-dialog
      class="dialog-wrapper to-complete-note-dialog"
      :visible.sync="bulkDialogVisible"
      append-to-body
    >
      <ContentHeader
        slot="title"
        :title="$t('monitoringDashboard.toComplete.notes.dialogTitle')"
      />
      <el-form label-position="top">
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="24">
            <FormItem>
              <el-alert
                type="warning"
                :closable="false"
                show-icon
                :title="
                  $t('monitoringDashboard.toComplete.notes.affectedRows', {
                    count: selectedRows.length,
                  })
                "
              />
            </FormItem>
          </el-col>
        </el-row>
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="24">
            <FormItem :label="$t('monitoringDashboard.toComplete.notes.label')">
              <el-input
                v-model="bulkNote"
                type="textarea"
                :rows="4"
                :maxlength="noteMaxLength"
                show-word-limit
                :placeholder="
                  $t('monitoringDashboard.toComplete.notes.placeholder')
                "
              />
            </FormItem>
          </el-col>
        </el-row>
        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="bulkDialogVisible = false"
          >
            {{ $t('monitoringDashboard.toComplete.notes.cancel') }}
          </el-button>
          <el-button
            type="primary"
            :loading="savingNote"
            @click.prevent="saveBulkNote"
          >
            {{ $t('monitoringDashboard.toComplete.notes.confirm') }}
          </el-button>
        </FormButtons>
      </el-form>
    </el-dialog>
  </div>
</template>

<script>
  import axios from 'axios';
  import debounce from 'lodash.debounce';
  import { mapActions } from 'vuex';
  import { monitoringApi } from '@/api';
  import acl from '@/services/acl';
  import tableHelper from '@/services/tableHelper';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import FormItem from '@/components/FormItem.vue';
  import {
    TO_COMPLETE_I18N_BASE,
    toCompleteColumns,
    toCompleteColumnFilters,
    toCompleteFilters,
  } from '@/config/monitoringToComplete.config';
  import { STABILITY_FILTER_DEFAULT } from '@/config/stabilityFilter';

  const PAGE_SIZE = 20;
  const PLACEHOLDER = '-';
  // Opening on the longest-expired items is the point of the section, so the
  // first page is already sorted and the header arrow has to say so.
  const DEFAULT_SORT = { prop: 'daysRemaining', order: 'ascending' };
  const DEFAULT_ORDER = 'daysRemaining ASC';
  const NOTE_PERMISSIONS = ['dashboardMonitoringNoteManage'];
  const NOTE_MAX_LENGTH = 2000;
  const NOTE_IDS_LIMIT = 500;

  const STATUS_TAG_TYPE = {
    pending: 'info',
    accepted: '',
    inprogress: 'warning',
    suspended: 'warning',
    toBeVerified: 'warning',
    toBeApproved: 'warning',
    completed: 'success',
    canceled: 'danger',
    rejected: 'danger',
  };

  function dayStartIso(day) {
    return new Date(`${day}T00:00:00`).toISOString();
  }

  function dayEndIso(day) {
    return new Date(`${day}T23:59:59.999`).toISOString();
  }

  export default {
    name: 'ToCompleteTable',
    components: {
      ContentHeader,
      FormButtons,
      FormItem,
    },
    props: {
      // Applied filters, never the draft the bar is editing: null until the
      // first search.
      filters: {
        type: Object,
        default: null,
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        columns: toCompleteColumns,
        i18nBase: TO_COMPLETE_I18N_BASE,
        placeholder: PLACEHOLDER,
        noteMaxLength: NOTE_MAX_LENGTH,
        defaultSort: DEFAULT_SORT,
        rows: [],
        total: 0,
        page: 1,
        limit: PAGE_SIZE,
        order: DEFAULT_ORDER,
        error: null,
        abortController: null,
        debouncedSort: null,
        selectedRows: [],
        editingNote: null,
        pendingNoteEdit: null,
        rowClickBlocked: false,
        savingNote: false,
        bulkDialogVisible: false,
        bulkNote: '',
        horizontalScrollTarget: null,
      };
    },
    created() {
      this.debouncedSort = debounce(this.onSortChange, 300);
      this.emitOrder();
    },
    mounted() {
      this.horizontalScrollTarget = this.$refs.table.$el.querySelector(
        '.el-table__body-wrapper'
      );
      this.$refs.table.$el.addEventListener('wheel', this.onTableWheel, {
        passive: false,
      });
    },
    beforeDestroy() {
      this.debouncedSort.cancel();
      this.abort();
      this.$refs.table.$el.removeEventListener('wheel', this.onTableWheel);
    },
    watch: {
      // The parent replaces the object on search / clear, so an identity watch
      // is enough and a deep one would fire on every keystroke of the draft.
      // The sort survives a refilter on purpose: el-table only reads
      // `default-sort` on first render, so resetting `order` here would leave
      // the header arrow pointing at a column the request no longer sorts by.
      // reserve-selection outlives the reload, so a refilter would otherwise
      // leave rows selected that the new result set no longer contains.
      // immediate: true so filters hydrated from a previous session.
      filters: {
        immediate: true,
        handler() {
          this.page = 1;
          this.clearSelection();
          this.emitCriteria();
          this.reload();
        },
      },
    },
    computed: {
      // "Nothing asked for yet" and "asked, nothing matched" are different
      // answers and must not share a message.
      emptyText() {
        if (this.error) return this.error;
        if (!this.hasSearch)
          return this.$t('monitoringDashboard.toComplete.beforeSearch');
        return this.$t('monitoringDashboard.toComplete.empty');
      },
      hasSearch() {
        const range = this.filters && this.filters.acceptedAt;
        return !!(range && range.length === 2 && range[0] && range[1]);
      },
      // The backend's 403 stays the authority: this only keeps the operator
      // from typing into a field the write would refuse.
      canManageNotes() {
        return acl.userCanUse(NOTE_PERMISSIONS);
      },
    },
    methods: {
      ...mapActions(['addTab']),
      isFilled(value) {
        return value !== null && value !== undefined && value !== '';
      },
      cellText(value) {
        return this.isFilled(value) ? value : PLACEHOLDER;
      },
      tagType(column, value) {
        const modifier = column.getClassModifier
          ? column.getClassModifier(value)
          : column.decode(value);
        return STATUS_TAG_TYPE[modifier] || '';
      },
      // A row with no estimated delivery date has no countdown to show. It also
      // has to sink to the bottom of the list.
      hasEstimate(row) {
        return this.isFilled(row.expectedDeliveryDate);
      },
      daysRemainingLabel(row) {
        if (!this.hasEstimate(row)) return PLACEHOLDER;
        return this.cellText(row.daysRemaining);
      },
      isExpired(row) {
        return this.hasEstimate(row) && Number(row.daysRemaining) < 0;
      },
      rowClassName({ row }) {
        return row.isStability ? 'to-complete-table__row--stability' : '';
      },
      baseParams() {
        const params = {
          acceptedFrom: dayStartIso(this.filters.acceptedAt[0]),
          acceptedTo: dayEndIso(this.filters.acceptedAt[1]),
        };
        const locationIds = (this.filters.locationId || []).map((e) => e.key);
        if (locationIds.length) params.locationIds = locationIds;
        const laboratoryCodes = (this.filters.laboratory || []).map(
          (e) => e.key
        );
        if (laboratoryCodes.length) params.laboratories = laboratoryCodes;
        if (this.filters.showParents) params.showParents = true;
        if (this.filters.showUnsignedRevisions)
          params.showUnsignedRevisions = true;
        params.stabilityFilter =
          this.filters.stabilityFilter || STABILITY_FILTER_DEFAULT;
        return params;
      },
      // Column filters go through tableHelper so the chip shape maps to the
      // same `and`/`or` where clause every other table produces.
      buildWhere() {
        const chipFilters = {};
        const operatorConditions = [];

        toCompleteColumnFilters.forEach((field) => {
          const entries = this.filters[field.name];
          if (!entries || !entries.length) return;

          if (field.type === 'dateRange') {
            if (entries[0] && entries[1]) {
              chipFilters[field.name] = [{ key: entries }];
            }
            return;
          }

          if (field.operators) {
            operatorConditions.push({
              or: entries.map((entry) => ({
                [field.name]:
                  entry.operator === '='
                    ? Number(entry.value)
                    : { [entry.operator]: Number(entry.value) },
              })),
            });
            return;
          }

          chipFilters[field.name] = entries;
        });

        const where = tableHelper.whereFilters(chipFilters, toCompleteFilters);
        if (!operatorConditions.length) return where;
        return { and: [...(where.and || []), ...operatorConditions] };
      },
      // The unpaginated criteria: the KPI cards and the Excel export both read
      // this instead of rebuilding a `where` of their own.
      criteria() {
        if (!this.hasSearch) return null;
        const params = this.baseParams();
        const where = this.buildWhere();
        if (Object.keys(where).length) params.where = where;
        return params;
      },
      emitCriteria() {
        this.$emit('criteria-change', this.criteria());
      },
      // Sort travels on its own event: it moves the export's ordering but not
      // the KPI totals, so it must not trigger a summary refetch.
      emitOrder() {
        this.$emit('order-change', this.order);
      },
      listParams() {
        const filter = {
          skip: (this.page - 1) * this.limit,
          limit: this.limit,
        };
        if (this.order) filter.order = this.order;
        const where = this.buildWhere();
        if (Object.keys(where).length) filter.where = where;
        return { ...this.baseParams(), filter };
      },
      abort() {
        if (this.abortController) this.abortController.abort();
        this.abortController = null;
      },
      setLoading(value) {
        this.$emit('update:loading', value);
      },
      async reload() {
        this.abort();
        this.editingNote = null;

        if (!this.hasSearch) {
          this.rows = [];
          this.total = 0;
          this.error = null;
          return;
        }

        this.abortController = new AbortController();
        const { signal } = this.abortController;
        this.error = null;
        this.setLoading(true);

        let aborted = false;
        try {
          const [listResponse, countResponse] = await Promise.all([
            monitoringApi.toComplete(this.listParams(), { signal }),
            monitoringApi.toCompleteCount(this.criteria(), { signal }),
          ]);
          const { data } = listResponse;
          this.rows = Array.isArray(data) ? data : data.rows || [];
          this.total = countResponse.data.count || 0;
        } catch (error) {
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }
          this.rows = [];
          this.total = 0;
          this.error = this.errorMessage(error);
        } finally {
          if (!aborted) this.setLoading(false);
        }
      },
      errorMessage(error) {
        return (
          (error.response &&
            error.response.data &&
            error.response.data.error &&
            error.response.data.error.message) ||
          error.message ||
          this.$t('global.error')
        );
      },
      notifyMessage(message) {
        this.$notify({
          showClose: true,
          type: 'error',
          message,
        });
      },
      notifyError(error) {
        this.notifyMessage(this.errorMessage(error));
      },
      onPageChange(page) {
        this.page = page;
        this.reload();
      },
      onSortChange({ prop, order }) {
        this.order =
          order && prop
            ? `${prop} ${order === 'ascending' ? 'ASC' : 'DESC'}`
            : null;
        this.page = 1;
        this.emitOrder();
        this.reload();
      },
      onSelectionChange(selection) {
        this.selectedRows = selection;
      },
      // Lets the mouse wheel pan the table horizontally while hovering over
      // it
      onTableWheel(event) {
        const target = this.horizontalScrollTarget;
        if (!target || event.deltaX !== 0) return;
        if (target.scrollWidth <= target.clientWidth) return;
        const max = target.scrollWidth - target.clientWidth;
        // Already at an edge in the wheel's direction: let the page scroll
        // instead of trapping the wheel on a table that can't pan further.
        if (event.deltaY > 0 && target.scrollLeft >= max) return;
        if (event.deltaY < 0 && target.scrollLeft <= 0) return;
        target.scrollLeft += event.deltaY;
        event.preventDefault();
      },
      clearSelection() {
        this.selectedRows = [];
        if (this.$refs.table) this.$refs.table.clearSelection();
      },
      noteText(value) {
        return this.isFilled(value) ? String(value) : '';
      },
      // The view now joins to the user table, so noteUpdatedBy already
      // arrives as the username.
      noteAuthor(row) {
        return this.cellText(row.noteUpdatedBy);
      },
      hasNoteMeta(row) {
        return (
          this.isFilled(row.noteUpdatedBy) || this.isFilled(row.noteUpdatedAt)
        );
      },
      isEditingNote(row) {
        return !!this.editingNote && this.editingNote.rowId === row.id;
      },
      noteInputRef() {
        const { noteInput } = this.$refs;
        return Array.isArray(noteInput) ? noteInput[0] : noteInput;
      },
      focusNoteInput() {
        this.$nextTick(() => {
          const input = this.noteInputRef();
          if (input) input.focus();
        });
      },
      startNoteEdit(row, key) {
        if (!this.canManageNotes || row.id == null) return;
        // The previous cell's blur already fired and is mid-save: queue this
        // request instead of dropping it, so one click still lands once that
        // save resolves rather than requiring the user to click twice.
        if (this.savingNote) {
          this.pendingNoteEdit = { rowId: row.id, key };
          return;
        }
        const original = this.noteText(row[key]);
        this.editingNote = { rowId: row.id, value: original, original };
        this.focusNoteInput();
      },
      resumePendingNoteEdit() {
        if (!this.pendingNoteEdit) return;
        const { rowId, key } = this.pendingNoteEdit;
        this.pendingNoteEdit = null;
        const row = this.rows.find((candidate) => candidate.id === rowId);
        if (row) this.startNoteEdit(row, key);
      },
      // Esc drops the edit synchronously so the blur it triggers finds no
      // pending edit and bails out instead of saving what was cancelled.
      onNoteCancel() {
        this.editingNote = null;
      },
      onNoteBlur() {
        if (!this.editingNote || this.savingNote) return;
        this.saveNote();
      },
      async saveNote() {
        if (!this.editingNote || this.savingNote) return;
        const { rowId, value, original } = this.editingNote;
        if (value.trim() === original.trim()) {
          this.editingNote = null;
          return;
        }
        const saved = await this.saveNotes([rowId], value);
        if (saved) this.editingNote = null;
      },
      selectedIds() {
        return this.selectedRows
          .map((row) => row.id)
          .filter((id) => this.isFilled(id));
      },
      // Splitting an oversized selection into several calls would trade away
      // the all-or-nothing write the cap exists to protect.
      withinIdLimit(count) {
        if (count <= NOTE_IDS_LIMIT) return true;
        this.notifyMessage(
          this.$t('monitoringDashboard.toComplete.notes.tooManyRows', {
            limit: NOTE_IDS_LIMIT,
          })
        );
        return false;
      },
      openBulkDialog() {
        if (!this.withinIdLimit(this.selectedRows.length)) return;
        this.bulkNote = '';
        this.bulkDialogVisible = true;
      },
      confirmBulkClear(note) {
        if (note.trim()) return Promise.resolve(true);
        return this.$confirm(
          this.$t('monitoringDashboard.toComplete.notes.confirmClear', {
            count: this.selectedRows.length,
          }),
          this.$t('monitoringDashboard.toComplete.notes.confirmClearTitle'),
          {
            type: 'warning',
            customClass: 'to-complete-note-confirm',
            confirmButtonText: this.$t('global.confirm'),
            cancelButtonText: this.$t('global.cancel'),
          }
        )
          .then(() => true)
          .catch(() => false);
      },
      async saveBulkNote() {
        const ids = this.selectedIds();
        if (!ids.length) return;
        const confirmed = await this.confirmBulkClear(this.bulkNote);
        if (!confirmed) return;
        const saved = await this.saveNotes(ids, this.bulkNote, {
          resetSelection: true,
        });
        if (saved) this.bulkDialogVisible = false;
      },
      // Single write path shared by the cell and the dialog
      async saveNotes(labSampleAnalysisIds, note, { resetSelection } = {}) {
        if (!this.withinIdLimit(labSampleAnalysisIds.length)) return false;
        const trimmed = note.trim().slice(0, NOTE_MAX_LENGTH);
        this.savingNote = true;
        try {
          await monitoringApi.toCompleteSaveNotes({
            labSampleAnalysisIds,
            note: trimmed,
          });
          this.$message({
            type: 'success',
            message: trimmed
              ? this.$t('monitoringDashboard.toComplete.notes.saved')
              : this.$t('monitoringDashboard.toComplete.notes.cleared'),
          });
          // reserve-selection outlives a reload on purpose, so the selection
          // has to go first.
          if (resetSelection) this.clearSelection();
          await this.reload();
          return true;
        } catch (error) {
          this.notifyError(error);
          return false;
        } finally {
          this.savingNote = false;
          this.resumePendingNoteEdit();
        }
      },
      onRootMouseDown() {
        const input = this.noteInputRef();
        this.rowClickBlocked =
          !!input && input.$el.contains(document.activeElement);
      },
      onRowClick(row, column) {
        if (this.rowClickBlocked || this.savingNote) return;
        if (column && column.type === 'selection') return;
        const labSampleId = row.labSampleId || row.id;
        if (!labSampleId) return;
        const query = { sampleIds: [labSampleId] };
        const params = { sampleId: String(labSampleId) };
        this.addTab({
          name: `sampleDetails${labSampleId}`,
          routeName: 'sampleDetails',
          path: '',
          query,
          params,
          meta: {
            label: `${this.$t('sampleDetails.tabLabel')} - ${
              row.labSampleCode || labSampleId
            }`,
          },
        });
        this.$router
          .push({ name: 'sampleDetails', query, params })
          .catch(() => {});
      },
    },
  };
</script>

<style lang="scss" scoped>
  .to-complete-table {
    width: 100%;

    &__note {
      display: flex;
      align-items: center;
      gap: 6px;
      margin: 20px 0 0;
      padding: 8px 12px;
      border-radius: 8px;
      background: rgba(#6366f1, 0.1);
      border: 1px solid rgba(#6366f1, 0.25);
      color: #c7d2fe;
      font-size: 12px;

      .el-icon-info {
        font-size: 14px;
      }
    }

    &__table {
      margin-top: 20px;
      cursor: pointer;
    }

    &__selection {
      margin-top: 20px;

      .el-button {
        margin-left: 12px;
      }
    }

    &__selection-count {
      color: #f8fafc;
      font-weight: 600;
    }

    &__selection-label {
      color: #cbd5e1;
    }

    &__note-meta {
      background-color: #2a2f4a;
      border-color: #323854;
      color: #cbd5e1;
    }

    &__pagination {
      margin-top: 12px;
      text-align: right;
    }

    &__days--expired {
      color: #f56c6c;
      font-weight: 600;
    }
  }

  // Same table skin as MonitoringDetailPanel / MonitoringFamilyBlock: el-table
  // defaults to a white surface, which reads as a hole in the dark dashboard.
  ::v-deep .el-table {
    background-color: #1e2235;
    border: 1px solid #323854;
    border-collapse: collapse;
    color: #cbd5e1;

    &::before,
    &::after {
      background-color: #323854;
    }

    thead tr th {
      background-color: #2a2f4a;
      color: #ffffff;
      font-weight: 600;
      padding: 8px 0;
      border-bottom: 1px solid #323854;
      text-align: center;
    }

    tbody tr td {
      background-color: #1e2235;
      padding: 4px 0;
      border-bottom: none;
      text-align: center;
    }

    .cell {
      text-align: center;
    }

    tbody tr:hover td {
      background-color: #2e3557;
      color: #ffffff;
    }

    tbody tr.to-complete-table__row--stability td {
      background-color: #1b3a4b;
    }

    tbody tr.to-complete-table__row--stability:hover td {
      background-color: #24506a;
    }

    &.el-table--border th,
    &.el-table--border td {
      border-right: 1px solid #323854;
    }

    // The empty block spans the full scroll width, so element's centering
    // parks the message off to the right of a 19-column table.
    .el-table__empty-block {
      background-color: #1e2235;
      justify-content: flex-start;
    }

    .el-table__empty-text {
      color: #64748b;
      width: auto;
      text-align: left;
      padding-left: 12px;
    }
  }
</style>

<style lang="scss">
  // The theme sets $--tooltip-fill and $--tooltip-color to the same #d8d8d8, so
  // a default popper prints invisible text.
  .el-tooltip__popper.to-complete-note-tooltip {
    color: #1e2235;
  }

  // Both mount on body, out of reach of the scoped block above.
  .to-complete-note-dialog {
    .el-textarea .el-input__count,
    .el-input__count-inner {
      background-color: transparent;
      color: #94a3b8;
    }
  }

  // $--color-warning is #522828 in the theme, so the box icon lands as a dark
  // maroon glyph on a dark panel.
  .el-message-box.to-complete-note-confirm {
    .el-message-box__status.el-icon-warning {
      color: #f59e0b;
    }
  }
</style>
